// src/components/compliance/KycIntroModal.tsx
/**
 * GwG identification dialog (STORY-990).
 *
 * Shown once per session on login to investors/sellers who still have to
 * identify themselves. Explanation → choice of method → visible check run.
 * Deliberately soft — «Später» is always allowed, there is no lock screen.
 *
 * STORY-991: after the return from the ident provider the dialog no longer
 * shows a bare result text but the running check (`KycProgress`) — every step
 * with its live status, polled until the run is done.
 *
 * STORY-1000: identification and Lex Koller are ONE compliance step. The
 * assessment is embedded as the second step of this dialog instead of opening
 * a second one, and what the identification already answered is not asked again.
 *
 * STORY-1139: an aborted self identification is never a dead end. `PENDING` no
 * longer means «wait» by default — as long as nothing was handed in, the dialog
 * offers to pick the capture up where it stopped.
 *
 * STORY-1140: whether that offer is made comes from `GET /kyc/status`
 * (`self_ident`, api#1042), not from the browser — on a second device the
 * remembered id is missing, and «fortsetzen» would start a second
 * identification behind an already submitted one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  ScanFace,
  ShieldCheck,
  X,
} from 'lucide-react'
import complianceApi, {
  lastKycSelfIdentId,
  markKycReturnPending,
  rememberKycRunId,
  rememberKycSelfIdentId,
} from '@/services/compliance'
import { authApi } from '@/services/api'
import { useAuthStore } from '@stores/authStore'
import KycCaptureWizard from './KycCaptureWizard'
import KycHandoffQr from './KycHandoffQr'
import KycProgress from './KycProgress'
import LexKollerModal from './LexKollerModal'
import {
  gwgEffectiveWording,
  isLikelyMobileDevice,
  KYC_HANDOFF_POLL_INTERVAL_MS,
  KYC_RUN_MAX_POLLS,
  KYC_RUN_POLL_INTERVAL_MS,
  KYC_RUN_STATUS,
  KYC_START_METHOD,
  KYC_STATUS,
  KYC_STEP_FOLLOW_UP_STATUSES,
  REGULATED_CATEGORIES,
  REGULATED_CATEGORY,
} from '@/constants/kyc'
import type {
  KycCheckRun,
  KycSelfApi,
  KycStartMethod,
  KycStatusResponse,
  RegulatedCategory,
} from '@/types/kyc'

export type KycStep =
  | 'intro'
  | 'choice'
  | 'regulated'
  | 'handoff'
  | 'capture'
  | 'submitted'
  | 'progress'
  | 'pending'
  | 'done'
  | 'lex_koller'

interface Props {
  isOpen: boolean
  /** Current KYC status; drives the initial step and the result screen. */
  status: KycStatusResponse | null
  onClose: () => void
  /** Optional «Später» handler — omit where the dialog was opened deliberately. */
  onSkip?: () => void
  /** Called whenever a fresh status was loaded from the API. */
  onStatusChange?: (status: KycStatusResponse) => void
  /** Step to open on; defaults to the explanation. */
  initialStep?: KycStep
  /**
   * Whether the Lex Koller assessment is still open (STORY-1000). If so it is
   * shown as the second step of this dialog — never as a second dialog.
   */
  lexKollerRequired?: boolean
  /** Result of the embedded Lex Koller assessment. */
  onLexKollerComplete?: (status: string) => void
  /** Reports the step the dialog is currently on. */
  onStepChange?: (step: KycStep) => void
}

export default function KycIntroModal({
  isOpen,
  status,
  onClose,
  onSkip,
  onStatusChange,
  initialStep = 'intro',
  lexKollerRequired = false,
  onLexKollerComplete,
  onStepChange,
}: Props) {
  const { t, i18n } = useTranslation()
  const { companies, user } = useAuthStore()

  const [step, setStep] = useState<KycStep>(initialStep)
  const [loading, setLoading] = useState(false)

  // Visible check run (STORY-991)
  const [run, setRun] = useState<KycCheckRun | null>(null)
  const [runFailed, setRunFailed] = useState(false)
  const [runTimedOut, setRunTimedOut] = useState(false)
  const [revealDone, setRevealDone] = useState(false)
  const runStartedRef = useRef(false)
  const pollsRef = useRef(0)

  // Self identification: capture wizard in this dialog (STORY-1070)
  const [selfVerificationId, setSelfVerificationId] = useState<number | null>(null)
  const [livenessChallenge, setLivenessChallenge] = useState<string | null>(null)
  const [livenessFingerCount, setLivenessFingerCount] = useState<number | null>(null)
  // Phone handoff: the QR screen watches the same identification (STORY-1071)
  const [phoneStarted, setPhoneStarted] = useState(false)
  /**
   * Whether the waiting screen may offer to continue the identification
   * (STORY-1139). Stays `false` while the remembered identification is still
   * being asked — a button that quietly starts a second capture is worse than
   * a moment of waiting.
   */
  const [canResumeSelfIdent, setCanResumeSelfIdent] = useState(false)
  /**
   * The Erwerbsberechtigung is already answered (Issue #1165) — then the
   * second step is skipped, no matter what the opener still assumed.
   */
  const [lexKollerAssessed, setLexKollerAssessed] = useState(false)

  // Regulated self-declaration form
  const [companyId, setCompanyId] = useState<number | null>(null)
  const [category, setCategory] = useState<RegulatedCategory>(REGULATED_CATEGORY.REGULATED)
  const [authority, setAuthority] = useState('')
  const [reference, setReference] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const contactStatus = status?.contact?.status ?? KYC_STATUS.NONE
  const verifiedAt = status?.contact?.verified_at ?? null
  const declaredCompany = status?.company ?? null
  /**
   * What the status says about the open self identification (STORY-1140).
   *
   * `selfIdentReported` false means «the API does not say» — a missing field
   * (pre api#1042) or no status at all; only then the browser storage decides.
   * Two plain values on purpose: a refreshed status object is a new object every
   * time and must not restart the waiting-screen effect.
   */
  const selfIdentReported = status?.self_ident !== undefined
  const selfIdentSubmitted = status?.self_ident?.submitted === true

  // Companies the user may declare for — from the auth store, falling back to
  // the company the backend already resolved for this user.
  const selectableCompanies = companies.length
    ? companies.map((c) => ({ id: c.company_id, name: c.company_name }))
    : declaredCompany
      ? [{ id: declaredCompany.id, name: declaredCompany.name || '' }]
      : []

  // Reset to the right entry step every time the dialog opens. An explicit
  // entry point (settings) always wins; the auto-opened dialog ('intro')
  // adapts to a run that is already pending or finished.
  useEffect(() => {
    if (!isOpen) return
    // A refreshed status must never pull the user out of the Lex Koller step —
    // nor out of the running capture or its confirmation (STORY-1070), which a
    // fresh PENDING status would otherwise replace with the generic screen.
    if (
      step === 'lex_koller' ||
      step === 'handoff' ||
      step === 'capture' ||
      step === 'submitted'
    ) {
      return
    }
    if (initialStep !== 'intro') setStep(initialStep)
    else if (contactStatus === KYC_STATUS.VERIFIED) setStep('done')
    else if (contactStatus === KYC_STATUS.PENDING) setStep('pending')
    else setStep('intro')
    setConfirmed(false)
    setCompanyId((prev) => prev ?? selectableCompanies[0]?.id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialStep, contactStatus])

  // Let the opener follow along — it decides on close what still counts as open
  useEffect(() => {
    if (isOpen) onStepChange?.(step)
  }, [isOpen, step, onStepChange])

  // Forget the previous check run whenever the dialog is closed
  useEffect(() => {
    if (isOpen) return
    // …and the step, so the guard above does not pin a reopened dialog to the
    // Lex Koller step it was closed on
    setStep(initialStep)
    setRun(null)
    setRunFailed(false)
    setRunTimedOut(false)
    setRevealDone(false)
    setSelfVerificationId(null)
    setLivenessChallenge(null)
    setLivenessFingerCount(null)
    setPhoneStarted(false)
    setCanResumeSelfIdent(false)
    setLexKollerAssessed(false)
    runStartedRef.current = false
    pollsRef.current = 0
  }, [isOpen, initialStep])

  // Close on Escape — the dialog is never a lock screen
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  const refreshStatus = useCallback(async (): Promise<KycStatusResponse | null> => {
    try {
      const fresh = await complianceApi.getKycStatus()
      onStatusChange?.(fresh)
      return fresh
    } catch {
      return null
    }
  }, [onStatusChange])

  // Start the check run as soon as the progress screen is entered (STORY-991).
  // The run is created once per opened dialog — the ref guards React 18's
  // double-invoked effects in strict mode.
  useEffect(() => {
    if (!isOpen || step !== 'progress' || runStartedRef.current) return
    runStartedRef.current = true
    let cancelled = false
    complianceApi
      .runKycChecks()
      .then((started) => {
        if (cancelled) return
        setRun(started)
        if (user) rememberKycRunId(user.id, started.id)
      })
      .catch(() => {
        if (!cancelled) setRunFailed(true)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, user?.id])

  // Poll the run until it is done — real progress, no simulated bar
  useEffect(() => {
    if (!run || run.status !== KYC_RUN_STATUS.RUNNING) return
    // Safety net: the run never reported an end state. Stop waiting and hand
    // over to the manual check instead of freezing the dialog on «Läuft».
    if (pollsRef.current >= KYC_RUN_MAX_POLLS) {
      setRunTimedOut(true)
      return
    }
    const timer = setTimeout(() => {
      pollsRef.current += 1
      complianceApi
        .getKycCheckRun(run.id)
        .then(setRun)
        .catch(() => setRunFailed(true))
    }, KYC_RUN_POLL_INTERVAL_MS)
    return () => clearTimeout(timer)
  }, [run])

  // A finished run may have changed the identification status
  useEffect(() => {
    if (!run || run.status === KYC_RUN_STATUS.RUNNING) return
    refreshStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id, run?.status])

  const handleRevealComplete = useCallback(() => setRevealDone(true), [])

  const runFinished = !!run && run.status !== KYC_RUN_STATUS.RUNNING
  // We gave up polling — the run counts as over, the rest is done by hand.
  const runStalled = !!run && !runFinished && runTimedOut
  const runComplete = revealDone && (runFinished || runStalled)
  // A hit, an error or an open result means: we look at it by hand. The
  // customer never learns which step it was.
  const needsFollowUp =
    !run ||
    runStalled ||
    run.status === KYC_RUN_STATUS.FAILED ||
    run.steps.some((s) => KYC_STEP_FOLLOW_UP_STATUSES.includes(s.status))

  const formatDate = (value?: string | null): string => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const handleStart = async (method: KycStartMethod) => {
    setLoading(true)
    try {
      const session = await complianceApi.startKyc({
        method,
        return_url: window.location.href,
      })
      // Self identification stays here (STORY-1070): we capture the document
      // ourselves, so there is nothing to redirect to and nothing to come back
      // from — the wizard takes over in this very dialog.
      if (method === KYC_START_METHOD.SELF_IDENT) {
        setSelfVerificationId(session.verification_id)
        // Remember it: only this id tells a later `PENDING` apart — aborted
        // capture or already handed in (STORY-1139)
        if (user) rememberKycSelfIdentId(user.id, session.verification_id)
        setLivenessChallenge(session.liveness_challenge ?? null)
        setLivenessFingerCount(session.liveness_finger_count ?? null)
        setPhoneStarted(false)
        // At the desktop the phone question comes first (STORY-1071) — with
        // the code already on screen. On a phone it would be absurd, there the
        // assistant starts right away.
        setStep(isLikelyMobileDevice() ? 'capture' : 'handoff')
        setLoading(false)
        return
      }
      // The tab navigates away — the marker makes the dialog come back on the
      // check-run screen once the provider sends the user home (STORY-991).
      if (user) markKycReturnPending(user.id)
      // Provider (or the staging stub) takes over from here
      window.location.href = session.session_url
    } catch {
      toast.error(
        t('kyc.errors.startFailed', 'Die Identifikation konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.'),
      )
      setLoading(false)
    }
  }

  const handleDeclare = async () => {
    if (!companyId || !authority.trim() || !confirmed) return
    setLoading(true)
    try {
      await complianceApi.declareRegulated({
        company_id: companyId,
        category,
        authority: authority.trim(),
        reference: reference.trim() || null,
      })
      await refreshStatus()
      setStep('done')
    } catch {
      toast.error(
        t('kyc.errors.declareFailed', 'Die Bestätigung konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.'),
      )
    } finally {
      setLoading(false)
    }
  }

  /**
   * The three calls of the capture wizard, bound to the started verification.
   *
   * The wizard never imports the service itself — the follow-up ticket runs it
   * against the phone handoff session with the same interface.
   */
  const selfApi = useMemo<KycSelfApi | null>(() => {
    if (selfVerificationId == null) return null
    const id = selfVerificationId
    return {
      upload: (slot, file, filename, mode) =>
        complianceApi.uploadSelfIdentFile(id, slot, file, filename, mode),
      submit: (data) => complianceApi.submitSelfIdent(id, data),
      status: () => complianceApi.getSelfIdentStatus(id),
      analyze: () => complianceApi.analyzeSelfIdent(id),
    }
  }, [selfVerificationId])

  /**
   * Picks up the Lex Koller stand the identification may have settled for us
   * (Issue #1165).
   *
   * The analysis of the document reads the nationality — with a Swiss document
   * the API answers the Erwerbsberechtigung by itself, and then the second step
   * of this dialog is not asked at all.
   */
  const refreshLexKoller = useCallback(async () => {
    try {
      const { data } = await authApi.me()
      const assessed = (data as { lex_koller_status?: string | null } | null)?.lex_koller_status
      if (!assessed) return
      const current = useAuthStore.getState().user
      if (current) useAuthStore.getState().setUser({ ...current, lex_koller_status: assessed })
      setLexKollerAssessed(true)
    } catch {
      // Nothing learned — the step is then simply offered as before
    }
  }, [])

  const handleSelfIdentSubmitted = useCallback(() => {
    setStep('submitted')
    // The identification is PENDING from now on — badges and settings follow
    refreshStatus()
    void refreshLexKoller()
  }, [refreshStatus, refreshLexKoller])

  /**
   * While the QR code is on screen: keep an eye on the identification.
   *
   * The phone hands in without telling this tab, so the dialog asks — and
   * moves to «Eingereicht, wird geprüft» on its own (STORY-1071). Leaving the
   * step or closing the dialog stops the polling.
   */
  useEffect(() => {
    if (!isOpen || step !== 'handoff' || selfVerificationId == null) return
    let cancelled = false
    const timer = setInterval(() => {
      complianceApi
        .getSelfIdentStatus(selfVerificationId)
        .then((current) => {
          if (cancelled) return
          if (current.submitted_at) {
            setStep('submitted')
            refreshStatus()
          } else if (current.files && Object.values(current.files).some(Boolean)) {
            // A first file means: the code was redeemed, the phone is at work
            setPhoneStarted(true)
          }
        })
        .catch(() => {
          // A failed poll says nothing — the next one may well answer
        })
    }, KYC_HANDOFF_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [isOpen, step, selfVerificationId, refreshStatus])

  /**
   * On the waiting screen: is there anything left to continue (STORY-1139)?
   *
   * The self identification has no provider that reports a result later — an
   * identification that was never handed in stays open forever. Handed in
   * already means «a human is looking at it», everything else means «you can
   * carry on».
   *
   * STORY-1140: the answer comes from the status itself (`self_ident`, api#1042)
   * — it holds on a second device and after cleared storage, where the
   * remembered id would wrongly offer to carry on and quietly start a second
   * identification. The browser storage is only the fallback for an API that
   * does not send the field yet.
   */
  useEffect(() => {
    if (!isOpen || step !== 'pending') return
    if (selfIdentReported) {
      // `null` = no self identification behind this PENDING (provider route):
      // offering to carry on is still better than the dead end.
      if (selfIdentSubmitted) setStep('submitted')
      else setCanResumeSelfIdent(true)
      return
    }
    const remembered = user ? lastKycSelfIdentId(user.id) : null
    if (remembered == null) {
      // Nothing remembered (other device, cleared storage): offering to carry
      // on still beats the dead end — the API picks up an open identification
      // of this contact by itself.
      setCanResumeSelfIdent(true)
      return
    }
    let cancelled = false
    complianceApi
      .getSelfIdentStatus(remembered)
      .then((current) => {
        if (cancelled) return
        if (current.submitted_at) setStep('submitted')
        else setCanResumeSelfIdent(true)
      })
      .catch(() => {
        // Gone or not ours any more — the button starts a fresh capture
        if (!cancelled) setCanResumeSelfIdent(true)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, user?.id, selfIdentReported, selfIdentSubmitted])

  const handleRefresh = async () => {
    setLoading(true)
    const fresh = await refreshStatus()
    setLoading(false)
    if (fresh?.contact?.status === KYC_STATUS.VERIFIED) setStep('done')
  }

  if (!isOpen) return null

  // One compliance step, two stations (STORY-1000). The second station only
  // exists while the Lex Koller assessment is still open.
  const showLexKollerStep = (lexKollerRequired && !lexKollerAssessed) || step === 'lex_koller'
  const identSettled =
    step === 'progress' || step === 'pending' || step === 'done' || step === 'submitted'
  // The identification may have answered the question on its own (Issue #1165)
  const continuesToLexKoller = lexKollerRequired && identSettled && !lexKollerAssessed

  const ChoiceCard = ({
    onClick,
    icon,
    title,
    description,
    highlight = false,
    disabled = false,
    testId,
  }: {
    onClick: () => void
    icon: React.ReactNode
    title: string
    description: string
    highlight?: boolean
    disabled?: boolean
    testId: string
  }) => (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
        highlight
          ? 'border-primary-500 bg-primary-50 hover:bg-primary-100'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg flex-shrink-0 ${
            highlight ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className={`font-semibold ${highlight ? 'text-primary-700' : 'text-gray-900'}`}>{title}</p>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kyc-intro-modal-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="h-5 w-5 text-primary-600 flex-shrink-0" />
            <h2 id="kyc-intro-modal-title" className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {step === 'lex_koller'
                ? t('lexKoller.title', 'Lex Koller Einschätzung')
                : t('kyc.title', 'Einmalig identifizieren')}
            </h2>
          </div>
          <button
            data-testid="kyc-intro-modal-close-btn"
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Schliessen')}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-5">
          {/* One dialog, two stations: identification → right to acquire */}
          {showLexKollerStep && (
            <ol
              data-testid="kyc-intro-modal-stepper"
              aria-label={t('kyc.stepper.label', 'Fortschritt der Compliance-Prüfung')}
              className="flex items-center gap-2 text-xs sm:text-sm"
            >
              {[
                { key: 'ident', label: t('kyc.stepper.identification', '1 Identifikation') },
                { key: 'lex', label: t('kyc.stepper.lexKoller', '2 Erwerbsberechtigung') },
              ].map((station, index) => {
                const isCurrent =
                  station.key === 'lex' ? step === 'lex_koller' : step !== 'lex_koller'
                const isDone = station.key === 'ident' && step === 'lex_koller'
                return (
                  <li key={station.key} className="flex items-center gap-2 min-w-0">
                    {index > 0 && <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />}
                    <span
                      aria-current={isCurrent ? 'step' : undefined}
                      className={`px-2.5 py-1 rounded-full truncate ${
                        isCurrent
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : isDone
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {station.label}
                    </span>
                  </li>
                )
              })}
            </ol>
          )}

          {/* Step 1 — explanation */}
          {step === 'intro' && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t('kyc.intro.title', 'Willkommen bei Immotron — einmalig identifizieren')}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {/* «Ab dem …» until the law takes effect, «Seit dem …» after */}
                {gwgEffectiveWording() === 'before'
                  ? t(
                      'kyc.intro.bodyBefore',
                      'Sie sind zum ersten Mal hier. Ab dem 1. Oktober 2026 verlangt das Schweizer Geldwäschereigesetz, dass wir unsere Vertragspartner einmal identifizieren — so wie Ihre Bank. Das dauert zwei Minuten und gilt dauerhaft.',
                    )
                  : t(
                      'kyc.intro.bodyAfter',
                      'Sie sind zum ersten Mal hier. Seit dem 1. Oktober 2026 verlangt das Schweizer Geldwäschereigesetz, dass wir unsere Vertragspartner einmal identifizieren — so wie Ihre Bank. Das dauert zwei Minuten und gilt dauerhaft.',
                    )}
              </p>
              <ul className="space-y-2">
                {[
                  t('kyc.intro.pointOnce', 'Einmal erledigt — unbefristet gültig.'),
                  t('kyc.intro.pointDuration', 'Zwei Minuten, kein Formular.'),
                ].map((point) => (
                  <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step 2 — choice of method */}
          {step === 'choice' && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t('kyc.choice.title', 'Wie möchten Sie sich identifizieren?')}
              </h3>
              <div className="space-y-3">
                {/* Self capture in this dialog — no provider, no redirect
                    (STORY-1070). The SwissID card stays out until an ident
                    provider is decided (api#874); its texts remain. */}
                <ChoiceCard
                  testId="kyc-intro-modal-auto-ident-btn"
                  onClick={() => handleStart(KYC_START_METHOD.SELF_IDENT)}
                  icon={<ScanFace size={20} />}
                  title={t('kyc.choice.autoIdent', 'Ausweis + Selfie (2 Min.)')}
                  description={t(
                    'kyc.choice.autoIdentDesc',
                    'Identitätskarte oder Pass vor die Kamera, danach ein Selfie.',
                  )}
                  highlight
                />
                <ChoiceCard
                  testId="kyc-intro-modal-regulated-btn"
                  onClick={() => setStep('regulated')}
                  icon={<Building2 size={20} />}
                  title={t('kyc.choice.regulated', 'Meine Firma ist beaufsichtigt oder kotiert')}
                  description={t(
                    'kyc.choice.regulatedDesc',
                    'Bank, Versicherung, Fonds, Pensionskasse oder kotierte Gesellschaft — eine Bestätigung genügt.',
                  )}
                />
              </div>
            </div>
          )}

          {/* Step 2b — regulated self-declaration */}
          {step === 'regulated' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('kyc.regulated.title', 'Beaufsichtigte Gegenpartei')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t(
                    'kyc.regulated.description',
                    'Ist Ihre Firma beaufsichtigt, kotiert oder öffentlich-rechtlich, genügt Ihre Bestätigung — eine persönliche Identifikation entfällt.',
                  )}
                </p>
              </div>

              {selectableCompanies.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {t('kyc.regulated.noCompany', 'Ihrem Konto ist keine Firma zugeordnet. Bitte identifizieren Sie sich als Person oder melden Sie sich bei uns.')}
                </p>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor="kyc-regulated-company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t('kyc.regulated.company', 'Firma')}
                    </label>
                    <select
                      data-testid="kyc-intro-modal-company-select"
                      id="kyc-regulated-company"
                      value={companyId ?? ''}
                      onChange={(e) => setCompanyId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {selectableCompanies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-2">
                      {t('kyc.regulated.category', 'Kategorie')}
                    </legend>
                    <div className="space-y-2">
                      {REGULATED_CATEGORIES.map((cat) => (
                        <label
                          key={cat}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            category === cat
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            data-testid="kyc-intro-modal-category-radio"
                            type="radio"
                            name="kyc-regulated-category"
                            value={cat}
                            checked={category === cat}
                            onChange={() => setCategory(cat)}
                            className="mt-1 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">
                            {t(`kyc.regulated.categories.${cat}`, cat)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div>
                    <label
                      htmlFor="kyc-regulated-authority"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t('kyc.regulated.authority', 'Aufsichtsbehörde oder Quelle')}
                    </label>
                    <input
                      data-testid="kyc-intro-modal-authority-input"
                      id="kyc-regulated-authority"
                      type="text"
                      value={authority}
                      onChange={(e) => setAuthority(e.target.value)}
                      maxLength={200}
                      placeholder={t('kyc.regulated.authorityPlaceholder', 'z. B. FINMA, OAK BV, SIX')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="kyc-regulated-reference"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t('kyc.regulated.reference', 'Bewilligungs- oder Registernummer')}
                      <span className="text-gray-400 font-normal"> ({t('common.optional', 'optional')})</span>
                    </label>
                    <input
                      data-testid="kyc-intro-modal-reference-input"
                      id="kyc-regulated-reference"
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      maxLength={200}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                    <input
                      data-testid="kyc-intro-modal-confirm-checkbox"
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="mt-0.5 rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      {t(
                        'kyc.regulated.confirm',
                        'Ich bestätige, dass die genannte Firma beaufsichtigt, kotiert oder öffentlich-rechtlich ist.',
                      )}
                    </span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* Step 2c — the phone question, with the code already there
              (STORY-1071). Only at the desktop; the phone skips it. */}
          {step === 'handoff' && selfVerificationId != null && (
            <KycHandoffQr
              verificationId={selfVerificationId}
              phoneStarted={phoneStarted}
              onContinueOnComputer={() => setStep('capture')}
            />
          )}

          {/* Step 2d — capture the document ourselves (STORY-1070) */}
          {step === 'capture' && selfApi && (
            <KycCaptureWizard
              api={selfApi}
              livenessChallenge={livenessChallenge}
              livenessFingerCount={livenessFingerCount}
              onSubmitted={handleSelfIdentSubmitted}
              onBack={() => setStep('choice')}
            />
          )}

          {/* Handed in — a human looks at it, nobody has to wait here */}
          {step === 'submitted' && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <Clock className="h-12 w-12 text-amber-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">
                {t('kyc.submitted.title', 'Eingereicht, wird geprüft')}
              </h3>
              <p className="text-sm text-gray-600">
                {t(
                  'kyc.submitted.body',
                  'Wir prüfen Ihre Angaben und melden uns. Sie können die Plattform weiter nutzen.',
                )}
              </p>
            </div>
          )}

          {/* Step 3 — the check run, visible step by step (STORY-991) */}
          {step === 'progress' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('kyc.progress.heading', 'Einen Moment — wir prüfen Ihre Identität')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t(
                    'kyc.progress.lead',
                    'Sie sehen hier live, womit wir abgleichen. Das dauert nur wenige Sekunden.',
                  )}
                </p>
              </div>

              {runFailed && !run ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {t(
                    'kyc.progress.failed',
                    'Die Prüfung konnte nicht gestartet werden. Wir prüfen Ihre Angaben manuell und melden uns bei Ihnen.',
                  )}
                </p>
              ) : (
                <>
                  <KycProgress
                    run={run}
                    stalled={runStalled}
                    onRevealComplete={handleRevealComplete}
                  />

                  {runComplete && (
                    <div
                      data-testid="kyc-intro-modal-run-outcome"
                      className={`flex items-start gap-3 p-4 rounded-lg border ${
                        needsFollowUp
                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      }`}
                    >
                      {needsFollowUp ? (
                        <Clock size={20} className="flex-shrink-0" />
                      ) : (
                        <BadgeCheck size={20} className="flex-shrink-0" />
                      )}
                      <div className="min-w-0 text-sm">
                        <p className="font-medium">
                          {needsFollowUp
                            ? t('kyc.progress.followUpTitle', 'Fast geschafft')
                            : t('kyc.progress.doneTitle', 'Identifiziert')}
                        </p>
                        <p className="opacity-80 mt-0.5">
                          {needsFollowUp
                            ? t(
                                'kyc.progress.followUpBody',
                                'Wir prüfen Ihre Angaben noch manuell und melden uns innert einem Arbeitstag bei Ihnen.',
                              )
                            : verifiedAt
                              ? t('kyc.done.verifiedAt', 'Identifiziert am {{date}}', {
                                  date: formatDate(verifiedAt),
                                })
                              : t(
                                  'kyc.done.generic',
                                  'Ihre Identifikation ist erfasst und gilt unbefristet.',
                                )}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Identification started — either still to be continued by the
              customer (STORY-1139) or waiting for a result */}
          {step === 'pending' && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <Clock className="h-12 w-12 text-amber-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">
                {canResumeSelfIdent
                  ? t('kyc.pending.resumeTitle', 'Identifikation nicht abgeschlossen')
                  : t('kyc.pending.title', 'Identifikation läuft')}
              </h3>
              <p className="text-sm text-gray-600">
                {canResumeSelfIdent
                  ? t(
                      'kyc.pending.resumeBody',
                      'Ihre Identifikation ist gestartet, aber noch nicht eingereicht. Sie können dort weitermachen, wo Sie aufgehört haben.',
                    )
                  : t(
                      'kyc.pending.body',
                      'Ihre Identifikation ist gestartet, aber noch nicht bestätigt. Sobald das Ergebnis vorliegt, sehen Sie es hier und in den Einstellungen.',
                    )}
              </p>
              {canResumeSelfIdent && (
                <button
                  data-testid="kyc-intro-modal-resume-btn"
                  type="button"
                  onClick={() => handleStart(KYC_START_METHOD.SELF_IDENT)}
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      {t('kyc.pending.resume', 'Identifikation fortsetzen')}
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              )}
              <div>
                <button
                  data-testid="kyc-intro-modal-refresh-btn"
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50"
                >
                  {t('kyc.pending.refresh', 'Status aktualisieren')}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — result */}
          {step === 'done' && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <BadgeCheck className="h-12 w-12 text-emerald-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">
                {t('kyc.done.title', 'Danke — das war es schon')}
              </h3>
              {contactStatus === KYC_STATUS.VERIFIED && verifiedAt ? (
                <p className="text-sm text-gray-600">
                  {t('kyc.done.verifiedAt', 'Identifiziert am {{date}}', { date: formatDate(verifiedAt) })}
                </p>
              ) : declaredCompany ? (
                <p className="text-sm text-gray-600">
                  {t('kyc.done.regulatedConfirmed', '{{company}} ist als beaufsichtigte Gegenpartei erfasst.', {
                    company: declaredCompany.name || t('kyc.done.yourCompany', 'Ihre Firma'),
                  })}
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  {t('kyc.done.generic', 'Ihre Identifikation ist erfasst und gilt unbefristet.')}
                </p>
              )}
            </div>
          )}

          {/* Step 4 — right to acquire (Lex Koller), same dialog (STORY-1000) */}
          {step === 'lex_koller' && (
            <LexKollerModal
              embedded
              isOpen
              kycStatus={status}
              onClose={onClose}
              onSkip={onSkip}
              onComplete={(lexStatus) => onLexKollerComplete?.(lexStatus)}
            />
          )}
        </div>

        {/* Footer — the embedded assessment brings its own */}
        {step !== 'lex_koller' && (
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-100">
          {step === 'intro' && onSkip ? (
            <button
              data-testid="kyc-intro-modal-skip-btn"
              type="button"
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
            >
              {t('kyc.later', 'Später')}
            </button>
          ) : step === 'choice' || step === 'regulated' || step === 'handoff' ? (
            // The capture step is missing on purpose — the assistant carries
            // its own «Zurück», two of them side by side would only confuse.
            <button
              data-testid="kyc-intro-modal-back-btn"
              type="button"
              onClick={() => setStep(step === 'choice' ? 'intro' : 'choice')}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft size={16} />
              {t('common.back', 'Zurück')}
            </button>
          ) : (
            <div />
          )}

          {step === 'capture' ||
          step === 'handoff' ||
          (step === 'pending' && canResumeSelfIdent && !continuesToLexKoller) ? (
            // Assistant, QR screen and the «fortsetzen» screen bring their own
            // action — a second button in the same colour only competes (R3)
            <div />
          ) : step === 'intro' ? (
            <button
              data-testid="kyc-intro-modal-next-btn"
              type="button"
              onClick={() => setStep('choice')}
              className="flex items-center gap-1 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              {t('kyc.intro.start', 'Jetzt identifizieren')}
              <ChevronRight size={16} />
            </button>
          ) : step === 'regulated' ? (
            <button
              data-testid="kyc-intro-modal-declare-btn"
              type="button"
              onClick={handleDeclare}
              disabled={loading || !companyId || !authority.trim() || !confirmed}
              className="flex items-center gap-1 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : t('kyc.regulated.submit', 'Bestätigen')}
            </button>
          ) : step === 'progress' ? (
            <button
              data-testid="kyc-intro-modal-continue-btn"
              type="button"
              onClick={continuesToLexKoller ? () => setStep('lex_koller') : onClose}
              disabled={!runComplete && !runFailed}
              className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('kyc.progress.continue', 'Weiter')}
            </button>
          ) : continuesToLexKoller ? (
            <button
              data-testid="kyc-intro-modal-to-lex-koller-btn"
              type="button"
              onClick={() => setStep('lex_koller')}
              disabled={loading}
              className="flex items-center gap-1 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {t('common.next', 'Weiter')}
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              data-testid="kyc-intro-modal-close-footer-btn"
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : t('common.close', 'Schliessen')}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  )
}
